import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { bk, nok, errMsg, custToken, STATUS_LABEL } from "@/lib/booking";
import { useSeo } from "@/lib/seo";
import { Tracker } from "@/components/site/Tracker";

const AGR = { forespurt: "Forespurt", tilbud_sendt: "Tilbud sendt", aktiv: "Aktiv", pauset: "Pauset", avsluttet: "Avsluttet", avslatt: "Avslått" };

const btn = "rounded-full bg-[#2F5D50] px-6 py-2.5 font-bold text-white hover:bg-[#244a40]";

const AuthForm = ({ onDone }) => {
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", name: "", phone: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = mode === "login" ? await bk.login({ email: f.email, password: f.password }) : await bk.register(f);
      localStorage.setItem("cust_token", r.token);
      onDone();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="mx-auto max-w-sm space-y-3" data-testid="customer-auth-form">
      <h1 className="font-heading text-3xl font-extrabold">{mode === "login" ? "Logg inn" : "Opprett konto"}</h1>
      {mode === "register" && <><Input data-testid="cust-name-input" required placeholder="Navn" value={f.name} onChange={set("name")} />
        <Input data-testid="cust-phone-input" placeholder="Telefon" value={f.phone} onChange={set("phone")} /></>}
      <Input data-testid="cust-email-input" type="email" required placeholder="E-post" value={f.email} onChange={set("email")} />
      <Input data-testid="cust-password-input" type="password" required minLength={6} placeholder="Passord" value={f.password} onChange={set("password")} />
      <button data-testid="cust-auth-submit" className={`${btn} w-full`}>{mode === "login" ? "Logg inn" : "Opprett konto"}</button>
      <button type="button" data-testid="cust-auth-toggle" onClick={() => setMode(mode === "login" ? "register" : "login")} className="w-full text-sm text-stone-500">
        {mode === "login" ? "Ny kunde? Opprett konto" : "Har du konto? Logg inn"}
      </button>
    </form>
  );
};

export default function Account() {
  const [me, setMe] = useState(null);
  const [list, setList] = useState([]);
  const [authed, setAuthed] = useState(!!custToken());
  const [agr, setAgr] = useState([]);
  useSeo({ title: "Min side – Ask-Stokke Renhold", description: "Dine bestillinger", noindex: true });
  const load = () => Promise.all([bk.me(), bk.myBookings()]).then(([m, l]) => { setMe({ ...m, address: m.addresses?.[0] || "" }); setList(l); bk.myAgreements().then(setAgr).catch(() => {}); })
    .catch(() => { localStorage.removeItem("cust_token"); setAuthed(false); });
  useEffect(() => { if (authed) load(); }, [authed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!authed) return <main className="px-5 py-16"><AuthForm onDone={() => setAuthed(true)} /></main>;
  if (!me) return <div className="p-10 text-stone-500">Laster...</div>;

  const save = async () => { await bk.updateMe({ name: me.name, phone: me.phone, addresses: me.address ? [me.address] : [] }); toast.success("Lagret"); };
  const cancel = async (b) => {
    if (!window.confirm("Avbestille denne bestillingen?")) return;
    try { await bk.cancel(b.id); toast.success("Avbestilt"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const today = new Date().toISOString().slice(0, 10);

  return (
    <main className="mx-auto max-w-4xl px-5 py-10" data-testid="account-page">
      <div className="flex items-center">
        <h1 className="font-heading text-3xl font-extrabold">Hei, {me.name.split(" ")[0]}</h1>
        <button data-testid="cust-logout" onClick={() => { localStorage.removeItem("cust_token"); setAuthed(false); setMe(null); }} className="ml-auto text-sm text-stone-500 hover:text-stone-800">Logg ut</button>
      </div>
      <section className="mt-6 rounded-2xl border border-stone-200 bg-white p-5">
        <h2 className="font-heading font-bold mb-3">Mine bestillinger</h2>
        {list.length ? list.map((b) => (
          <div key={b.id} data-testid={`my-booking-${b.id}`} className="flex flex-wrap items-center gap-3 border-b border-stone-100 py-3 last:border-0 text-sm">
            <div className="flex-1 min-w-[200px]"><b>{b.service_name}</b> · {b.date} kl. {b.start}<div className="text-stone-500">{b.address} · {b.ref}</div></div>
            <span className="rounded-full bg-stone-100 px-2.5 py-0.5 text-xs font-semibold">{STATUS_LABEL[b.status]}</span>
            <b>{nok(b.price?.total)}</b>
            <div className="basis-full"><Tracker status={b.status} /></div>
            {b.status === "bekreftet" && b.date > today && <button onClick={() => cancel(b)} className="text-xs text-red-600">Avbestill</button>}
          </div>
        )) : <p className="text-sm text-stone-500">Ingen bestillinger ennå. <Link to="/bestill" className="underline">Bestill renhold</Link></p>}
      </section>
      {agr.length > 0 && (
        <section className="mt-4 rounded-2xl border border-stone-200 bg-white p-5" data-testid="my-agreements">
          <h2 className="font-heading font-bold mb-3">Faste avtaler</h2>
          {agr.map((a) => (
            <div key={a.id} className="flex flex-wrap gap-3 border-b border-stone-100 py-2 last:border-0 text-sm">
              <b>{a.ref}</b><span className="flex-1">{a.address}</span>
              <span className="rounded-full bg-stone-100 px-2.5 py-0.5 text-xs font-semibold">{AGR[a.status]}</span><b>{nok(a.price_per_visit)}/besøk</b>
            </div>
          ))}
        </section>
      )}
      <section className="mt-4 rounded-2xl border border-stone-200 bg-white p-5 grid gap-3 sm:grid-cols-3">
        <h2 className="sm:col-span-3 font-heading font-bold">Kontaktinformasjon</h2>
        <Input data-testid="profile-name" value={me.name} onChange={(e) => setMe({ ...me, name: e.target.value })} />
        <Input data-testid="profile-phone" placeholder="Telefon" value={me.phone || ""} onChange={(e) => setMe({ ...me, phone: e.target.value })} />
        <Input data-testid="profile-address" placeholder="Lagret adresse" value={me.address} onChange={(e) => setMe({ ...me, address: e.target.value })} />
        <button data-testid="profile-save" onClick={save} className={`${btn} w-fit`}>Lagre</button>
      </section>
    </main>
  );
}
